import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation } from 'react-router-dom';

import { useAuth } from '@/lib/auth/AuthProvider';

interface LoginPageLocationState {
  from?: { pathname?: string };
}

/**
 * Login landing — kicks off the Keycloak login flow.
 *
 * Behaviour:
 *  - while auth bootstrap is still in-flight, render nothing (avoids flicker)
 *  - if already authenticated, bounce to the originally requested route (or `/`)
 *  - otherwise show a single button that triggers `keycloak.login()`
 */
export function LoginPage() {
  const { t } = useTranslation('auth');
  const { initialised, isAuthenticated, login } = useAuth();
  const location = useLocation();
  const from = (location.state as LoginPageLocationState | null)?.from?.pathname ?? '/';

  useEffect(() => {
    // If the URL still carries OAuth response params (code/state/error),
    // we're mid-flow — Keycloak.init() is about to process them. Wait.
    const hasOAuthParams =
      /[?#][^]*\b(code|state|error|session_state)=/.test(window.location.href);
    if (initialised && !isAuthenticated && !hasOAuthParams) {
      // Auto-trigger the redirect; the button below is a fallback in case
      // pop-up blockers or routing oddities prevent the auto-call.
      void login();
    }
  }, [initialised, isAuthenticated, login]);

  if (!initialised) return <div className="h-screen w-screen" aria-busy="true" />;
  if (isAuthenticated) return <Navigate to={from} replace />;

  return (
    <div className="flex min-h-screen items-center justify-center bg-brand-50">
      <div className="rounded-lg border border-slate-200 bg-white p-8 shadow-sm text-center">
        <h1 className="text-display text-brand-900">{t('appName')}</h1>
        <p className="mt-2 text-body text-slate-600">{t('redirecting')}</p>
        <button
          type="button"
          onClick={() => void login()}
          className="mt-6 rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2"
        >
          {t('signInWith')}
        </button>
      </div>
    </div>
  );
}
